import { useMutation, useQuery } from '@apollo/client';
import CustomReactSelect, { Option } from 'components/custom/CustomReactSelect';
import { UserContext } from 'contexts/user/UserContext';
import { useSettings } from 'contexts/SettingsContextUtils';
import IntlMessages from 'helpers/IntlMessages';
import { getTranslatedString } from 'helpers/utils-typescript';
import React, {
  useContext,
  useEffect,
  useState
} from 'react';
import { Alert, Button, Dropdown, Form } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import {
  Community,
  CommunityContributionProject,
  UserContribution,
  UserContributionTimer
} from 'services/graphqltypes';
import ContributionWorkTypeSelect, { ProjectWorkType } from '../Community/components/ContributionWorkTypeSelect';
import ContributionTimerHourglassIcon from './ContributionTimerHourglassIcon';
import {
  CONTRIBUTION_TIMER_OPTIONS,
  DISCARD_CONTRIBUTION_TIMER,
  FINALIZE_CONTRIBUTION_TIMER,
  RUNNING_CONTRIBUTION_TIMER,
  PAUSE_CONTRIBUTION_TIMER,
  RESUME_CONTRIBUTION_TIMER,
  START_CONTRIBUTION_TIMER
} from './queries';
import {
  calculateActiveElapsedSeconds,
  formatElapsedTime
} from './timerUtils';

type RunningTimer = Pick<
  UserContributionTimer,
  | 'id'
  | 'startedAt'
  | 'pausedAt'
  | 'pausedDurationSeconds'
>;

type RunningContributionTimerData = {
  runningTimer?: {
    nodes: RunningTimer[];
  };
};

type StartContributionTimerData = {
  startContributionTimer?: {
    userContributionTimer?: RunningTimer | null;
  } | null;
};

type FinalizeContributionTimerData = {
  finalizeContributionTimer?: {
    userContributionTimer?: {
      userContribution?: Pick<
        UserContribution,
        'effortMinutes' | 'ipAmount' | 'status'
      > | null;
    } | null;
  } | null;
};

type ContributionTimerOptionsData = {
  contributionTypes?: { nodes: ProjectWorkType[] };
  currentUser?: {
    communityUsers?: {
      nodes: {
        communityId?: string | null;
        community?: Pick<Community, 'id' | 'name'> | null;
      }[];
    } | null;
  } | null;
  communityContributionProjects?: {
    nodes: (Pick<
      CommunityContributionProject,
      'id' | 'name' | 'shortDescription' | 'primaryCommunityId'
    > & {
      allowedContributionTypes: { nodes: ProjectWorkType[] };
      canManage: boolean;
      primaryCommunity: Pick<Community, 'slug'>;
      additionalHostCommunities: { nodes: Pick<Community, 'id'>[] };
    })[];
  } | null;
};

type PauseContributionTimerData = {
  pauseContributionTimer?: {
    userContributionTimer?: RunningTimer | null;
  } | null;
};

type ResumeContributionTimerData = {
  resumeContributionTimer?: {
    userContributionTimer?: RunningTimer | null;
  } | null;
};

type TimerIdVariables = {
  timerId: string;
};

type FinalizeTimerVariables = TimerIdVariables & {
  communityId: string;
  projectId: string;
  description: string;
  contributionTypeId: string;
};

const ContributionTimerLauncher = (): React.ReactElement | null => {
  const { isAuthenticated } = useContext(UserContext);
  const { locale: currentLang } = useSettings();
  const intl = useIntl();
  const [showPopover, setShowPopover] = useState(false);
  const [showFinalizationForm, setShowFinalizationForm] =
    useState(false);
  const [showDiscardConfirmation, setShowDiscardConfirmation] =
    useState(false);
  const [communityId, setCommunityId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [contributionTypeId, setContributionTypeId] = useState('');
  const [showTypeHelp, setShowTypeHelp] = useState(false);
  const [description, setDescription] = useState('');
  const [currentTime, setCurrentTime] = useState(() => Date.now());

  const { data, refetch, loading: timerLoading, error: timerError } = useQuery<RunningContributionTimerData>(
    RUNNING_CONTRIBUTION_TIMER,
    { skip: !isAuthenticated, notifyOnNetworkStatusChange: true }
  );

  const [
    startContributionTimer,
    {
      loading: isStarting,
      error: startError,
      reset: resetStartContributionTimer
    }
  ] = useMutation<
    StartContributionTimerData,
    Record<string, never>
  >(START_CONTRIBUTION_TIMER, {
    update: (cache, result) => {
      const timer = result.data?.startContributionTimer?.userContributionTimer;
      if (!timer) return;
      const previous = cache.readQuery<RunningContributionTimerData>({ query: RUNNING_CONTRIBUTION_TIMER });
      // Start must add the new timer to the query even if its subsequent refresh fails.
      cache.writeQuery<RunningContributionTimerData>({
        query: RUNNING_CONTRIBUTION_TIMER,
        data: { ...previous, runningTimer: { ...previous?.runningTimer, nodes: [timer] } }
      });
    }
  });

  const {
    data: optionsData,
    loading: optionsLoading,
    error: optionsError
  } = useQuery<ContributionTimerOptionsData>(
    CONTRIBUTION_TIMER_OPTIONS,
    {
      skip: !isAuthenticated || !showPopover,
      fetchPolicy: 'network-only'
    }
  );

  const [
    pauseContributionTimer,
    {
      loading: isPausing,
      error: pauseError,
      reset: resetPauseContributionTimer
    }
  ] = useMutation<PauseContributionTimerData, TimerIdVariables>(
    PAUSE_CONTRIBUTION_TIMER
  );

  const [
    resumeContributionTimer,
    {
      loading: isResuming,
      error: resumeError,
      reset: resetResumeContributionTimer
    }
  ] = useMutation<ResumeContributionTimerData, TimerIdVariables>(
    RESUME_CONTRIBUTION_TIMER
  );

  const [
    finalizeContributionTimer,
    {
      data: finalizeData,
      loading: isFinalizing,
      error: finalizeError,
      reset: resetFinalizeContributionTimer
    }
  ] = useMutation<FinalizeContributionTimerData, FinalizeTimerVariables>(
    FINALIZE_CONTRIBUTION_TIMER
  );

  const [
    discardContributionTimer,
    {
      loading: isDiscarding,
      error: discardError,
      reset: resetDiscardContributionTimer
    }
  ] = useMutation<unknown, TimerIdVariables>(
    DISCARD_CONTRIBUTION_TIMER
  );

  const runningTimer = data?.runningTimer?.nodes[0];
  const runningTimerId = runningTimer?.id;
  const pausedAt = runningTimer?.pausedAt;
  const isPaused = Boolean(pausedAt);
  const joinedCommunities = (
    optionsData?.currentUser?.communityUsers?.nodes ?? []
  )
    .map((membership) => membership.community)
    .filter(
      (community): community is Pick<Community, 'id' | 'name'> =>
        Boolean(community)
    );
  const joinedCommunityIds = new Set(
    joinedCommunities.map((community) => community.id)
  );
  const availableTypeIds = new Set((optionsData?.contributionTypes?.nodes ?? []).map((type) => type.id));
  const eligibleProjects = (
    optionsData?.communityContributionProjects?.nodes ?? []
  ).filter(
    (project) =>
      project.allowedContributionTypes.nodes.some((type) => availableTypeIds.has(type.id)) && (
        joinedCommunityIds.has(project.primaryCommunityId) ||
        project.additionalHostCommunities.nodes.some((host) =>
          joinedCommunityIds.has(host.id)
        )
      )
  );
  const selectedProjects = eligibleProjects.filter(
    (project) =>
      project.primaryCommunityId === communityId ||
      project.additionalHostCommunities.nodes.some(
        (host) => host.id === communityId
      )
  );
  const communityOptions: Option[] = joinedCommunities.map((community) => ({
    label: getTranslatedString(community.name, currentLang),
    value: community.id
  }));
  const projectOptions: Option[] = selectedProjects.map((project) => ({
    label: getTranslatedString(project.name, currentLang),
    value: project.id
  }));
  const selectedProject = selectedProjects.find((project) => project.id === projectId);
  const selectedTypes = (selectedProject?.allowedContributionTypes.nodes ?? [])
    .filter((type) => availableTypeIds.has(type.id));
  const canFinalize =
    !timerLoading && !timerError &&
    !optionsError &&
    Boolean(pausedAt) &&
    Boolean(runningTimerId) &&
    Boolean(communityId) &&
    selectedTypes.some((type) => type.id === contributionTypeId) &&
    selectedProjects.some((project) => project.id === projectId) &&
    description.trim().length > 0 &&
    description.length <= 200;

  useEffect(() => {
    setShowFinalizationForm(false);
    setShowDiscardConfirmation(false);
    setCommunityId('');
    setProjectId('');
    setContributionTypeId('');
    setDescription('');
  }, [runningTimerId]);

  useEffect(() => {
    if (!runningTimerId || pausedAt) {
      return undefined;
    }

    setCurrentTime(Date.now());

    const intervalId = window.setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [pausedAt, runningTimerId]);

  if (!isAuthenticated) {
    return null;
  }

  const isRunning = Boolean(runningTimer);
  const elapsedSeconds = runningTimer
    ? calculateActiveElapsedSeconds({
        startedAt: runningTimer.startedAt,
        pausedAt: runningTimer.pausedAt,
        pausedDurationSeconds:
          runningTimer.pausedDurationSeconds,
        currentTime
      })
    : 0;
  const launcherLabelId = isPaused
    ? 'contribution-tracking.timer-paused'
    : isRunning
      ? 'contribution-tracking.timer-running'
      : 'contribution-tracking.page-title';
  const launcherState = isPaused
    ? 'paused'
    : isRunning
      ? 'running'
      : 'idle';

  const handleStart = (): void => {
    if (eligibleProjects.length === 0) {
      return;
    }

    resetFinalizeContributionTimer();
    resetDiscardContributionTimer();
    resetPauseContributionTimer();
    resetResumeContributionTimer();
    setShowFinalizationForm(false);
    setShowDiscardConfirmation(false);

    startContributionTimer()
      .then(() => refetch())
      .catch(() => undefined);
  };

  const handleStop = (): void => {
    if (!runningTimer) {
      return;
    }

    setShowDiscardConfirmation(false);
    if (runningTimer.pausedAt) {
      setShowFinalizationForm(true);
      return;
    }

    resetResumeContributionTimer();
    pauseContributionTimer({
      variables: { timerId: runningTimer.id }
    })
      .then(() => refetch())
      .then(() => setShowFinalizationForm(true))
      .catch(() => undefined);
  };

  const handlePause = (): void => {
    if (!runningTimer) {
      return;
    }

    resetResumeContributionTimer();
    setShowFinalizationForm(false);

    pauseContributionTimer({
      variables: { timerId: runningTimer.id }
    })
      .then(() => refetch())
      .catch(() => undefined);
  };

  const handleResume = (): void => {
    if (!runningTimer) {
      return;
    }

    resetPauseContributionTimer();
    setShowFinalizationForm(false);
    setShowDiscardConfirmation(false);

    resumeContributionTimer({
      variables: { timerId: runningTimer.id }
    })
      .then(() => refetch())
      .catch(() => undefined);
  };

  const handleFinalize = (): void => {
    if (!runningTimerId || !canFinalize) {
      return;
    }

    finalizeContributionTimer({
      variables: {
        timerId: runningTimerId,
        communityId,
        projectId,
        contributionTypeId,
        description: description.trim()
      },
      refetchQueries: 'active',
      onQueryUpdated: (query) =>
        query.queryName === 'ContributionTimerPage'
    })
      .then(() => {
        resetStartContributionTimer();
        resetPauseContributionTimer();
        resetResumeContributionTimer();
        setShowFinalizationForm(false);
        setCommunityId('');
        setProjectId('');
        setContributionTypeId('');
        setDescription('');
        return refetch();
      })
      .catch(() => undefined);
  };

  const handleDiscard = (): void => {
    if (!runningTimerId) {
      return;
    }

    discardContributionTimer({
      variables: { timerId: runningTimerId },
      refetchQueries: 'active',
      onQueryUpdated: (query) =>
        query.queryName === 'ContributionTimerPage'
    })
      .then(() => {
        resetStartContributionTimer();
        resetPauseContributionTimer();
        resetResumeContributionTimer();
        setShowFinalizationForm(false);
        setShowDiscardConfirmation(false);
        setCommunityId('');
        setProjectId('');
        setContributionTypeId('');
        setDescription('');
        return refetch();
      })
      .catch(() => undefined);
  };

  return (
    <Dropdown
      alignRight
      onToggle={(isOpen) => {
        setShowPopover(isOpen);
        if (isOpen) void refetch().catch(() => undefined);
      }}
      show={showPopover}
    >
      <Dropdown.Toggle
        aria-controls="contribution-timer-popover"
        aria-haspopup="dialog"
        aria-label={intl.formatMessage({ id: launcherLabelId })}
        className={`contribution-timer-launcher contribution-timer-launcher--${launcherState}`}
        id="contribution-timer-launcher"
        variant="link"
      >
        <ContributionTimerHourglassIcon
          animated={isRunning}
          size={30}
        />
        {isPaused && (
          <span
            aria-hidden="true"
            className="contribution-timer-launcher__pause-indicator"
          />
        )}
      </Dropdown.Toggle>

      {showPopover && (
        <Dropdown.Menu
          className="contribution-timer-popover mt-1 p-0"
          id="contribution-timer-popover"
          role="dialog"
        >
          <span
            aria-hidden="true"
            className="contribution-timer-popover__arrow"
          />
          <div
            className="d-flex align-items-center justify-content-between border-bottom px-3 py-2"
          >
            <strong id="contribution-timer-popover-title">
              <IntlMessages id="contribution-tracking.page-title" />
            </strong>
            <button
              aria-label={intl.formatMessage({ id: 'general.close' })}
              className="close ml-3"
              onClick={() => setShowPopover(false)}
              type="button"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div className="p-3">
            {timerError && <Alert variant="danger"><IntlMessages id="general.loading-error" /></Alert>}
            {startError && (
              <Alert variant="danger">
                <IntlMessages id="contribution-tracking.start-error" />
              </Alert>
            )}

            {pauseError && (
              <Alert variant="danger">
                <IntlMessages id="contribution-tracking.pause-error" />
              </Alert>
            )}

            {resumeError && (
              <Alert variant="danger">
                <IntlMessages id="contribution-tracking.resume-error" />
              </Alert>
            )}

            {optionsError && (
              <Alert variant="danger">
                <IntlMessages id="general.loading-error" />
              </Alert>
            )}

            {finalizeError && (
              <Alert variant="danger">
                <IntlMessages id="contribution-tracking.finalize-error" />
              </Alert>
            )}

            {discardError && (
              <Alert variant="danger">
                <IntlMessages id="contribution-tracking.discard-error" />
              </Alert>
            )}

            {finalizeData?.finalizeContributionTimer
              ?.userContributionTimer?.userContribution && (
              <Alert variant="success">
                <IntlMessages id="contribution-tracking.stop-success" />
                {finalizeData.finalizeContributionTimer
                  .userContributionTimer.userContribution.ipAmount > 0 && (
                  <div>
                    <IntlMessages
                      id="contribution-tracking.session-ip-awarded"
                      values={{
                        ipAmount:
                          finalizeData.finalizeContributionTimer
                            .userContributionTimer.userContribution
                            .ipAmount
                      }}
                    />
                  </div>
                )}
              </Alert>
            )}

            {runningTimer ? (
              <>
                <p>
                  <IntlMessages
                    id={
                      isPaused
                        ? 'contribution-tracking.timer-paused'
                        : 'contribution-tracking.timer-running'
                    }
                  />
                </p>
                <p className="h4">
                  {formatElapsedTime(elapsedSeconds)}
                </p>
                {showFinalizationForm && isPaused ? (
                  <Form
                    onSubmit={(event) => {
                      event.preventDefault();
                      handleFinalize();
                    }}
                  >
                    <Form.Group controlId="contribution-timer-community">
                      <Form.Label>
                        <IntlMessages id="contribution-tracking.community" />
                      </Form.Label>
                      <CustomReactSelect
                        name="contribution-timer-community"
                        inputId="contribution-timer-community"
                        options={communityOptions}
                        selected={communityOptions.find((option) => option.value === communityId) ?? null}
                        placeholder={intl.formatMessage({ id: 'contribution-tracking.choose-community' })}
                        handleChange={(option: Option | null) => {
                          setCommunityId(option?.value ?? '');
                          setProjectId('');
                          setContributionTypeId('');
                        }}
                      />
                    </Form.Group>
                    <Form.Group controlId="contribution-timer-project">
                      <Form.Label>
                        <IntlMessages id="contribution-tracking.project" />
                      </Form.Label>
                      <CustomReactSelect
                        name="contribution-timer-project"
                        inputId="contribution-timer-project"
                        options={projectOptions}
                        selected={projectOptions.find((option) => option.value === projectId) ?? null}
                        isDisabled={!communityId}
                        placeholder={intl.formatMessage({ id: 'contribution-tracking.choose-project' })}
                        handleChange={(option: Option | null) => {
                          setProjectId(option?.value ?? '');
                          setContributionTypeId('');
                        }}
                      />
                      {communityId && selectedProjects.length === 0 && (
                        <small className="text-muted">
                          <IntlMessages id="contribution-tracking.no-projects-for-community" />
                        </small>
                      )}
                    </Form.Group>
                    <Form.Group>
                      <Form.Label htmlFor="contribution-timer-type">
                        <IntlMessages id="contribution-tracking.work-type" />
                      </Form.Label>
                      <Button type="button" variant="link" size="sm"
                        aria-label={intl.formatMessage({ id: 'contribution-tracking.type-help-label' })}
                        aria-expanded={showTypeHelp} aria-controls="contribution-timer-type-help"
                        onClick={() => setShowTypeHelp(!showTypeHelp)}>?</Button>
                      {showTypeHelp && <p id="contribution-timer-type-help" className="small">
                        <IntlMessages id="contribution-tracking.type-help" />
                      </p>}
                      <ContributionWorkTypeSelect
                        id="contribution-timer-type" types={selectedTypes} isMulti={false}
                        selectedIds={[contributionTypeId]} disabled={!selectedProject || optionsLoading}
                        describedBy={showTypeHelp ? 'contribution-timer-type-help' : undefined}
                        onChange={(ids) => setContributionTypeId(ids[0] ?? '')}
                      />
                      {selectedProject?.canManage && selectedProject.primaryCommunity?.slug && (
                        <Link className="d-block mt-2" onClick={() => setShowPopover(false)}
                          to={`/community/${selectedProject.primaryCommunity.slug}/edit#contribution-projects`}>
                          <IntlMessages id="contribution-tracking.project-settings" />
                        </Link>
                      )}
                    </Form.Group>
                    <Form.Group controlId="contribution-timer-description">
                      <Form.Label>
                        <IntlMessages id="contribution-tracking.work-description" />
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        maxLength={200}
                        onChange={(event) =>
                          setDescription(event.target.value)
                        }
                        rows={3}
                        value={description}
                      />
                    </Form.Group>
                    <Button
                      disabled={
                        !canFinalize ||
                        isFinalizing ||
                        optionsLoading
                      }
                      type="submit"
                    >
                      <IntlMessages
                        id={
                          isFinalizing
                            ? 'contribution-tracking.finalizing'
                            : 'contribution-tracking.complete'
                        }
                      />
                    </Button>
                    <Button
                      className="ml-2"
                      onClick={() => setShowFinalizationForm(false)}
                      type="button"
                      variant="link"
                    >
                      <IntlMessages id="general.close" />
                    </Button>
                  </Form>
                ) : (
                  <>
                    <Button
                      className="mr-2"
                      disabled={
                        timerLoading || Boolean(timerError) ||
                        isPausing ||
                        isResuming ||
                        isFinalizing ||
                        isDiscarding
                      }
                      onClick={isPaused ? handleResume : handlePause}
                      type="button"
                      variant={isPaused ? 'primary' : 'warning'}
                    >
                      <IntlMessages
                        id={
                          isPaused
                            ? isResuming
                              ? 'contribution-tracking.resuming'
                              : 'contribution-tracking.resume'
                            : isPausing
                              ? 'contribution-tracking.pausing'
                              : 'contribution-tracking.pause'
                        }
                      />
                    </Button>
                    <Button
                      disabled={
                        timerLoading || Boolean(timerError) ||
                        isPausing ||
                        isResuming ||
                        isFinalizing ||
                        isDiscarding
                      }
                      onClick={handleStop}
                      type="button"
                      variant="danger"
                    >
                      <IntlMessages id="contribution-tracking.stop" />
                    </Button>
                  </>
                )}
                <div className="mt-2">
                  {showDiscardConfirmation ? (
                    <>
                      <Button
                        disabled={isDiscarding || timerLoading || Boolean(timerError)}
                        onClick={handleDiscard}
                        size="sm"
                        type="button"
                        variant="outline-danger"
                      >
                        <IntlMessages id="contribution-tracking.confirm-discard" />
                      </Button>
                      <Button
                        className="ml-2"
                        onClick={() => setShowDiscardConfirmation(false)}
                        size="sm"
                        type="button"
                        variant="link"
                      >
                        <IntlMessages id="general.cancel" />
                      </Button>
                    </>
                  ) : (
                    <Button
                      onClick={() => setShowDiscardConfirmation(true)}
                      size="sm"
                      type="button"
                      variant="link"
                    >
                      <IntlMessages id="contribution-tracking.discard" />
                    </Button>
                  )}
                </div>
              </>
            ) : (
              <>
                {optionsData && eligibleProjects.length === 0 && (
                  <Alert variant="info">
                    <IntlMessages id="contribution-tracking.no-projects" />
                    <Link
                      className="ml-1"
                      onClick={() => setShowPopover(false)}
                      to="/communities"
                    >
                      <IntlMessages id="contribution-tracking.find-community" />
                    </Link>
                  </Alert>
                )}
                <Button
                  disabled={
                    isStarting ||
                    timerLoading ||
                    Boolean(timerError) ||
                    Boolean(optionsError) ||
                    optionsLoading ||
                    eligibleProjects.length === 0
                  }
                  onClick={handleStart}
                  type="button"
                >
                  <IntlMessages id="contribution-tracking.start" />
                </Button>
              </>
            )}

            <Link
              className="btn btn-link ml-2"
              onClick={() => setShowPopover(false)}
              to="/profile#contributions"
            >
              <IntlMessages id="contribution-tracking.profile-action" />
            </Link>
          </div>
        </Dropdown.Menu>
      )}
    </Dropdown>
  );
};

export default ContributionTimerLauncher;
